import React, { useRef, useEffect, useState } from 'react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import '../styles/resizable-table.css';

interface ResizableTableHeaderProps {
  onResize?: (e: any, data: { size: { width: number } }) => void;
  width?: number;
  children: React.ReactNode;
  [key: string]: any;
}

export const ResizableTableHeader: React.FC<ResizableTableHeaderProps> = (props) => {
  const { onResize, width, children, ...restProps } = props;
  const headerRef = useRef<HTMLTableCellElement>(null);
  const [handleHeight, setHandleHeight] = useState<number | string>('100%');

  useEffect(() => {
    // Calculate actual table height for resize handle
    const updateHandleHeight = () => {
      const headerElement = headerRef.current;
      if (headerElement) {
        const tableWrapper = headerElement.closest('.ant-table-wrapper');
        if (tableWrapper) {
          const tableContainer = tableWrapper.querySelector('.ant-table-container');
          if (tableContainer) {
            // Get the actual height from table container (includes header + body)
            const containerRect = tableContainer.getBoundingClientRect();
            const headerRect = headerElement.getBoundingClientRect();
            // Calculate height from header top to container bottom
            const height = containerRect.bottom - headerRect.top;
            if (height > 0) {
              setHandleHeight(height);
            }
          }
        }
      }
    };

    // Initial calculation with multiple attempts to ensure table is rendered
    const timer1 = setTimeout(updateHandleHeight, 0);
    const timer2 = setTimeout(updateHandleHeight, 100);
    const timer3 = setTimeout(updateHandleHeight, 500);
    
    // Update on window resize and table content changes
    window.addEventListener('resize', updateHandleHeight);
    const observer = new MutationObserver(() => {
      // Debounce updates
      setTimeout(updateHandleHeight, 50);
    });
    
    const headerElement = headerRef.current;
    if (headerElement) {
      const tableWrapper = headerElement.closest('.ant-table-wrapper');
      if (tableWrapper) {
        observer.observe(tableWrapper, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
      }
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', updateHandleHeight);
      observer.disconnect();
    };
  }, []);

  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  const handleResizeStart = () => {
    // Add resizing class to all body cells in this column
    const headerElement = headerRef.current;
    if (headerElement) {
      const table = headerElement.closest('.ant-table');
      if (table) {
        const thead = table.querySelector('.ant-table-thead');
        const tbody = table.querySelector('.ant-table-tbody');
        if (thead && tbody) {
          const headers = Array.from(thead.querySelectorAll('th'));
          const columnIndex = headers.indexOf(headerElement);
          if (columnIndex >= 0) {
            const cells = tbody.querySelectorAll(`tr > td:nth-child(${columnIndex + 1})`);
            cells.forEach((cell: Element) => (cell as HTMLElement).classList.add('resizing'));
          }
        }
      }
    }
  };

  const handleResizeStop = () => {
    // Remove resizing class from all body cells
    const table = document.querySelector('.ant-table-tbody');
    if (table) {
      const cells = table.querySelectorAll('td.resizing');
      cells.forEach((cell) => cell.classList.remove('resizing'));
    }
  };

  const handleResize = (e: any, data: any) => {
    if (onResize) {
      // Ensure minimum width
      const newWidth = Math.max(data.size.width, 50);
      onResize(e, { ...data, size: { ...data.size, width: newWidth } });
    }
  };

  return (
    <Resizable
      width={width}
      height={0}
      onResize={handleResize}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      resizeHandles={['e']}
      handle={
        <span 
          className="resizable-table-handle" 
          style={{ 
            height: typeof handleHeight === 'number' ? `${handleHeight}px` : handleHeight,
            maxHeight: 'none'
          }}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      minConstraints={[50, 0]}
    >
      <th ref={headerRef} {...restProps} style={{ width, position: 'relative', overflow: 'visible' }}>
        {children}
      </th>
    </Resizable>
  );
};

